import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { DropdownMenu, type DropdownMenuItem } from "./DropdownMenu";
import { BottomSheet } from "../BottomSheet";

const dropdownVariants = cva(
  "bg-surface-fill-primary-normal border border-surface-outline-neutral-subtle rounded-large",
  {
    variants: {
      size: {
        small: "w-64",
        medium: "w-80",
        large: "w-96",
      },
    },
    defaultVariants: {
      size: "medium",
    },
  }
);

export type DropdownItem = DropdownMenuItem;

export interface DropdownProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /**
   * Trigger element that opens the dropdown
   */
  trigger?: React.ReactNode;
  /**
   * Items to display in the dropdown
   */
  items?: DropdownItem[];
  /**
   * Custom content to render instead of the default item list
   */
  customContent?: React.ReactNode;
  /**
   * Custom class name for custom content
   */
  customContentClassName?: string;
  /**
   * Section heading text
   */
  sectionHeading?: string;
  /**
   * Whether the dropdown is in loading state
   */
  isLoading?: boolean;
  /**
   * Whether to show empty/no results state
   */
  isEmpty?: boolean;
  /**
   * Empty state title
   */
  emptyTitle?: string;
  /**
   * Empty state description
   */
  emptyDescription?: string;
  /**
   * Empty state link text
   */
  emptyLinkText?: string;
  /**
   * Empty state link click handler
   */
  onEmptyLinkClick?: () => void;
  /**
   * Primary button text
   */
  primaryButtonText?: string;
  /**
   * Secondary button text
   */
  secondaryButtonText?: string;
  /**
   * Primary button click handler
   */
  onPrimaryClick?: () => void;
  /**
   * Secondary button click handler
   */
  onSecondaryClick?: () => void;
  /**
   * Dropdown size
   */
  size?: "small" | "medium" | "large";
  /**
   * Whether dropdown is open (controlled)
   */
  open?: boolean;
  /**
   * Default open state (uncontrolled)
   */
  defaultOpen?: boolean;
  /**
   * Callback when open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Custom class for dropdown container
   */
  containerClassName?: string;
  /**
   * Custom class for dropdown menu
   */
  menuClassName?: string;
  /**
   * Show chevron on list items
   */
  showChevron?: boolean;
  /**
   * Custom empty state icon
   */
  emptyIcon?: React.ReactNode;
  /**
   * Disable footer buttons
   */
  disableFooter?: boolean;
  /**
   * Whether to show footer actions (overrides disableFooter)
   */
  showFooter?: boolean;
}

export const Dropdown = React.forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      className,
      trigger,
      items = [],
      customContent,
      customContentClassName,
      sectionHeading,
      isLoading = false,
      isEmpty = false,
      emptyTitle = "No Search Results Found",
      emptyDescription = "Add description of what the user can search for here.",
      emptyLinkText = "Link to support site",
      onEmptyLinkClick,
      primaryButtonText = "Primary",
      secondaryButtonText = "Secondary",
      onPrimaryClick,
      onSecondaryClick,
      size = "medium",
      open: controlledOpen,
      defaultOpen = false,
      onOpenChange,
      containerClassName,
      menuClassName,
      showChevron = false,
      emptyIcon,
      disableFooter = false,
      showFooter,
      ...props
    },
    ref
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
    const isOpen =
      controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = React.useState({
      top: "100%",
      bottom: "auto",
      left: "0",
      right: "auto",
      maxHeight: "400px",
    });

    // Detect if we're on mobile (< 768px)
    const [isMobile, setIsMobile] = React.useState(false);
    
    React.useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };
      
      // Check on mount
      checkMobile();
      
      // Add resize listener
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleOpenChange = (newOpen: boolean) => {
      if (controlledOpen === undefined) {
        setUncontrolledOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    };

    const toggleOpen = () => {
      handleOpenChange(!isOpen);
    };

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          handleOpenChange(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen]);

    // Close on escape key
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleOpenChange(false);
        }
      };

      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        return () => {
          document.removeEventListener("keydown", handleEscape);
        };
      }
    }, [isOpen]);

    // Calculate and adjust dropdown position to keep it within viewport
    React.useEffect(() => {
      if (!isOpen || !dropdownRef.current || !menuRef.current) return;

      const calculatePosition = () => {
        const triggerRect = dropdownRef.current!.getBoundingClientRect();
        const menuElement = menuRef.current!;

        // Get menu dimensions (use a temporary measurement if needed)
        const menuRect = menuElement.getBoundingClientRect();
        const menuHeight = menuRect.height || 400; // fallback to max-height
        const menuWidth = menuRect.width;

        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        const spaceBelow = viewportHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        const spaceRight = viewportWidth - triggerRect.left;
        const spaceLeft = triggerRect.right;

        const position: {
          top: string;
          bottom: string;
          left: string;
          right: string;
          maxHeight: string;
        } = {
          top: "auto",
          bottom: "auto",
          left: "auto",
          right: "auto",
          maxHeight: "400px",
        };

        // Vertical positioning
        if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
          // Position below trigger
          position.top = "100%";
          position.bottom = "auto";
          position.maxHeight = `${Math.min(400, spaceBelow - 16)}px`;
        } else {
          // Position above trigger
          position.top = "auto";
          position.bottom = "100%";
          position.maxHeight = `${Math.min(400, spaceAbove - 16)}px`;
        }

        // Horizontal positioning
        if (spaceRight >= menuWidth) {
          // Align to left edge of trigger
          position.left = "0";
          position.right = "auto";
        } else if (spaceLeft >= menuWidth) {
          // Align to right edge of trigger
          position.left = "auto";
          position.right = "0";
        } else {
          // Not enough space on either side, try to center or align based on available space
          if (triggerRect.left + menuWidth > viewportWidth) {
            position.left = "auto";
            position.right = "0";
          } else {
            position.left = "0";
            position.right = "auto";
          }
        }

        setMenuPosition(position);
      };

      // Calculate position after menu is rendered
      calculatePosition();

      // Recalculate on window resize or scroll
      const handleResize = () => calculatePosition();
      const handleScroll = () => calculatePosition();

      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll, true);

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }, [isOpen]);

    const sizeMap = {
      small: "w-64",
      medium: "w-80",
      large: "w-96",
    };

    // Render dropdown menu content wrapped in BottomSheet for mobile
    const renderDropdownContent = () => (
      <DropdownMenu
        ref={ref}
        items={items}
        customContent={customContent}
        customContentClassName={customContentClassName}
        sectionHeading={sectionHeading}
        isLoading={isLoading}
        isEmpty={isEmpty}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyLinkText={emptyLinkText}
        onEmptyLinkClick={onEmptyLinkClick}
        primaryButtonText={primaryButtonText}
        secondaryButtonText={secondaryButtonText}
        onPrimaryClick={onPrimaryClick}
        onSecondaryClick={onSecondaryClick}
        showChevron={showChevron}
        emptyIcon={emptyIcon}
        disableFooter={disableFooter}
        showFooter={showFooter}
        onClose={() => handleOpenChange(false)}
        className={className}
        width={isMobile ? "full" : sizeMap[size]}
        maxHeight={menuPosition.maxHeight}
        unstyled={isMobile}
      />
    );

    return (
      <div
        ref={dropdownRef}
        className={cn("relative inline-block", containerClassName)}
        {...props}
      >
        {/* Trigger */}
        {trigger && (
          <div onClick={toggleOpen} className="cursor-pointer">
            {trigger}
          </div>
        )}

        {/* Mobile: BottomSheet */}
        {isMobile ? (
          <BottomSheet
            isOpen={isOpen}
            onClose={() => handleOpenChange(false)}
            title={sectionHeading}
            variant="default"
            showDragHandle={true}
            closeOnOverlayClick={true}
            closeOnEscape={true}
            closeOnSwipeDown={true}
          >
            {renderDropdownContent()}
          </BottomSheet>
        ) : (
          /* Desktop: Regular Dropdown Menu */
          isOpen && (
            <div
              ref={menuRef}
              className={cn("absolute z-50", menuClassName)}
              style={{
                top: menuPosition.top,
                bottom: menuPosition.bottom,
                left: menuPosition.left,
                right: menuPosition.right,
                marginTop: menuPosition.top === "100%" ? "0.5rem" : "0",
                marginBottom: menuPosition.bottom === "100%" ? "0.5rem" : "0",
              }}
            >
              {renderDropdownContent()}
            </div>
          )
        )}
      </div>
    );
  }
);

Dropdown.displayName = "Dropdown";

export { dropdownVariants };
